import React from 'react'
import { cn } from '@/lib/utils/cn'

/* ------------------------------------------------------------------ */
/*  Logo — brand mark + wordmark                                       */
/*  Links to "/" unless already on "/"                                 */
/*  See ARCHITECTURE.md §17                                            */
/* ------------------------------------------------------------------ */

export type LogoSize = 'sm' | 'md' | 'lg'

export interface LogoProps extends React.HTMLAttributes<HTMLElement> {
  /** Visual size: sm (28px), md (36px), lg (48px) */
  size?: LogoSize
  /** Current pathname — if "/" the logo won't be a link */
  currentPath?: string
  /** Whether to show the wordmark alongside the icon */
  showWordmark?: boolean
}

const sizeConfig: Record<LogoSize, { icon: number; text: string }> = {
  sm: { icon: 28, text: 'text-base' },
  md: { icon: 36, text: 'text-xl' },
  lg: { icon: 48, text: 'text-2xl' },
}

function LogoIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="shrink-0"
    >
      <rect width="48" height="48" rx="10" className="fill-primary-600" />
      <text
        x="50%"
        y="54%"
        dominantBaseline="middle"
        textAnchor="middle"
        className="fill-neutral-0 font-display"
        fontSize="28"
        fontWeight="700"
      >
        H
      </text>
    </svg>
  )
}

export function Logo({
  size = 'md',
  currentPath,
  showWordmark = true,
  className,
  ...props
}: LogoProps) {
  const config = sizeConfig[size]
  const isHome = currentPath === '/'

  const content = (
    <span className={cn('inline-flex items-center gap-2', className)} {...props}>
      <LogoIcon size={config.icon} />
      {showWordmark && (
        <span
          className={cn(
            'font-display font-bold tracking-tight text-primary-700',
            config.text,
          )}
        >
          Hjälptorget
        </span>
      )}
    </span>
  )

  if (isHome) {
    return content
  }

  return (
    <a
      href="/"
      className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
      aria-label="Hjälptorget — till startsidan"
    >
      {content}
    </a>
  )
}

Logo.displayName = 'Logo'
